import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Stack,
  Chip,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutoNotesIcon from '@mui/icons-material/Notes';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { API_BASE_URL } from '../../../config/config';

import VirtualMotorLogsModal from './VirtualMotorLogsModal';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

// Modern iOS-style Switch
const IOSSwitch = styled(Switch)(({ theme }) => ({
  width: 52,
  height: 28,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(24px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#34c759',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 24,
    height: 24,
    boxShadow: '0 2px 4px 0 rgba(0,0,0,0.2)',
  },
  '& .MuiSwitch-track': {
    borderRadius: 28 / 2,
    backgroundColor: '#e9e9ea',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const VirtualMotorCard = ({ motor, deviceCode, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);

  // Check if this motor is a sequencer/group logic
  const isSequencer = motor?.executionType === 'sequencer';

  const getTimeDisplay = () => {
    if (!motor.lastCommand?.timestamp) return '';
    const lastTime = dayjs(motor.lastCommand.timestamp);
    const now = dayjs();
    const totalMinutes = now.diff(lastTime, 'minute');
    if (totalMinutes < 1) return '';
    if (totalMinutes < 60) {
      return `for ${String(totalMinutes).padStart(2, '0')} Min`;
    } else {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `for ${String(hours).padStart(2, '0')} Hr ${String(minutes).padStart(2, '0')} Min`;
    }
  };

  const getStatusInfo = () => {
    if (motor.sumpError || motor.flowError || motor.pressureError || motor.timeoutError) {
      return { color: '#f44336', bgColor: '#ffebee', label: 'ERROR' };
    }
    switch (motor.status) {
      case 'ON': return { color: '#34c759', bgColor: '#e8f5e9', label: 'ON' };
      case 'OFF': return { color: '#ff9500', bgColor: '#fff3e0', label: 'OFF' };
      case 'PLEASE_WAIT': return { color: '#2196f3', bgColor: '#e3f2fd', label: 'WAIT' };
      default: return { color: '#8e8e93', bgColor: '#f5f5f5', label: 'UNKNOWN' };
    }
  };

  const statusInfo = getStatusInfo();

  const isControlDisabled = loading ||
    motor.sumpError ||
    motor.flowError ||
    motor.pressureError ||
    motor.timeoutError ||
    motor.status === 'PLEASE_WAIT';

  const handleToggle = (checked) => {
    const action = checked ? 'ON' : 'OFF';
    setPendingAction(action);
    setConfirmDialog(true);
  };

  const handleConfirm = async () => {
    setConfirmDialog(false);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}api/virtual/motor/${deviceCode}/${motor._id}/execute`,
        { action: pendingAction },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({
        open: true,
        message: `Motor ${pendingAction} command sent successfully`,
        severity: 'success'
      });
      setTimeout(() => { if (onRefresh) onRefresh(); }, 1500);
    } catch (error) {
      console.error('Error toggling motor:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || `Failed to ${pendingAction} motor`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setPendingAction(null);
    }
  };

  return (
    <>
      <Card
        elevation={0}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 4,
          border: `1px solid ${isSequencer ? '#1976d250' : statusInfo.color + '40'}`, // Blue tinted border for Sequencers
          backgroundColor: '#ffffff',
          boxShadow: isSequencer ? '0 4px 16px rgba(25, 118, 210, 0.08)' : '0 4px 16px rgba(0,0,0,0.04)',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            boxShadow: isSequencer ? '0 6px 20px rgba(25, 118, 210, 0.15)' : '0 6px 20px rgba(0,0,0,0.08)',
            transform: 'translateY(-2px)'
          }
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>

          {/* Header: Title and Action Icons */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  bgcolor: isSequencer ? '#e3f2fd' : statusInfo.bgColor,
                  color: isSequencer ? '#1976d2' : statusInfo.color,
                  p: 0.8,
                  borderRadius: 2,
                  display: 'flex'
                }}
              >
                <PowerSettingsNewIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50', fontSize: '1.1rem', wordBreak: 'break-word', lineHeight: 1.2 }}>
                  {motor.name}
                </Typography>
                {/* Custom Badge for Sequencers */}
                {isSequencer && (
                  <Chip
                    label="Logic Sequence"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      bgcolor: '#e3f2fd',
                      color: '#1565c0',
                      mt: 0.5
                    }}
                  />
                )}
              </Box>
            </Box>

            <Box display="flex" gap={0.5}>
              <Tooltip title="View Logs">
                <IconButton
                  size="small"
                  onClick={() => setIsLogsModalOpen(true)}
                  sx={{ color: '#388e3c', bgcolor: '#f0fdf4' }}
                >
                  <AutoNotesIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Control Box Area */}
          <Box
            sx={{
              bgcolor: '#fafafa',
              borderRadius: 3,
              p: 2,
              mb: 2,
              border: '1px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Stack direction="column" spacing={0.5}>
              <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600, letterSpacing: 0.5 }}>
                CURRENT STATUS
              </Typography>
              <Chip
                label={statusInfo.label}
                size="small"
                sx={{
                  bgcolor: statusInfo.bgColor,
                  color: statusInfo.color,
                  fontWeight: 800,
                  fontSize: '0.75rem',
                  height: 24,
                  width: 'fit-content'
                }}
              />
            </Stack>

            <Box display="flex" alignItems="center">
              {loading ? (
                <CircularProgress size={28} sx={{ color: statusInfo.color, mr: 1 }} />
              ) : (
                <IOSSwitch
                  checked={motor.status === 'ON'}
                  onChange={(e) => handleToggle(e.target.checked)}
                  disabled={isControlDisabled}
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 2, borderStyle: 'dashed' }} />

          {/* Footer: Time Info */}
          <Box sx={{ mt: 'auto' }}>
            {motor.lastCommand?.timestamp ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <AccessTimeIcon sx={{ fontSize: 16, color: '#95a5a6' }} />
                <Box>
                  <Typography variant="body2" sx={{ color: '#546e7a', fontSize: '0.85rem', fontWeight: 500 }}>
                    {dayjs(motor.lastCommand.timestamp).format('h:mm A, D MMM')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#95a5a6', display: 'block' }}>
                    {getTimeDisplay() ? `${dayjs(motor.lastCommand.timestamp).fromNow()} • ${getTimeDisplay()}` : dayjs(motor.lastCommand.timestamp).fromNow()}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ color: '#95a5a6', fontSize: '0.85rem', fontStyle: 'italic' }}>
                No recent activity logged
              </Typography>
            )}

            {/* Error Message rendering */}
            {(motor.sumpError || motor.flowError || motor.pressureError || motor.timeoutError) && (
              <Alert severity="error" icon={false} sx={{ mt: 2, py: 0, '& .MuiAlert-message': { p: 1, fontSize: '0.8rem', fontWeight: 600 } }}>
                ⚠️ Error detected in system
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700, color: '#1a237e' }}>
          Confirm Action
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.primary">
            Are you sure you want to turn <strong>{motor.name}</strong> {pendingAction}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDialog(false)} sx={{ color: '#757575', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disableElevation
            sx={{
              bgcolor: pendingAction === 'ON' ? '#34c759' : '#ff9500',
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
              '&:hover': { bgcolor: pendingAction === 'ON' ? '#2fb350' : '#e68a00' }
            }}
          >
            Confirm {pendingAction}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logs Modal */}
      <VirtualMotorLogsModal
        open={isLogsModalOpen}
        onClose={() => setIsLogsModalOpen(false)}
        motor={motor}
        deviceCode={deviceCode}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default VirtualMotorCard;
